import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Globe, Check } from "lucide-react";
import { motion } from "framer-motion";

/**
 * Editorial legal-page layout — dark, Palantir/Apple inspired.
 * Props:
 *  - lang: 'es' | 'en'
 *  - setLang: (l) => void
 *  - meta: { title, subtitle, lastUpdated }
 *  - sections: [{ id, heading, body: ReactNode }]
 *  - related: [{ href, label }]
 */
export const LegalLayout = ({ lang, setLang, meta, sections, related }) => {
  const [activeId, setActiveId] = useState(sections[0]?.id);
  const contentRef = useRef(null);

  // Scroll-spy: highlight the section currently in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  const handleAnchor = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" });
    }
  };

  const T = {
    es: {
      back: "Volver al inicio",
      index: "Índice",
      lastUpdated: "Última actualización",
      related: "Documentos relacionados",
      footer: "Nexum Business S.L. — Todos los derechos reservados.",
    },
    en: {
      back: "Back to home",
      index: "Contents",
      lastUpdated: "Last updated",
      related: "Related documents",
      footer: "Nexum Business S.L. — All rights reserved.",
    },
  }[lang];

  return (
    <div className="min-h-screen text-white" style={{ background: "#0f0f0f" }}>
      {/* Sticky mini header */}
      <header
        className="sticky top-0 z-40 border-b border-white/[0.06] backdrop-blur-xl"
        style={{ background: "rgba(15,15,15,0.75)" }}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-5">
            <Link
              to="/"
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-xs md:text-sm"
              data-testid="legal-back-home"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{T.back}</span>
            </Link>
            <span className="h-4 w-px bg-white/10 hidden sm:block" />
            <Link
              to="/"
              className="font-grotesk text-lg md:text-xl font-semibold tracking-tight text-white"
            >
              Nexum
            </Link>
          </div>

          {/* Language switch */}
          <div className="flex items-center gap-1 border border-white/10 rounded-full p-0.5">
            <button
              onClick={() => setLang("es")}
              className={`px-3 py-1 text-[11px] rounded-full transition-colors ${
                lang === "es" ? "bg-white text-black" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="legal-lang-es"
            >
              ES
            </button>
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1 text-[11px] rounded-full transition-colors ${
                lang === "en" ? "bg-white text-black" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="legal-lang-en"
            >
              EN
            </button>
          </div>
        </div>
      </header>

      {/* Hero / title */}
      <section className="border-b border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10 md:pb-16">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 mb-4 md:mb-6"
          >
            {lang === "es" ? "Documento legal" : "Legal document"}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="font-grotesk text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] max-w-3xl"
          >
            {meta.title}
          </motion.h1>
          {meta.subtitle && (
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-5 md:mt-6 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed"
            >
              {meta.subtitle}
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 md:mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 text-[11px] uppercase tracking-wider text-zinc-500"
          >
            <span>
              {T.lastUpdated}:{" "}
              <span className="text-zinc-300">{meta.lastUpdated}</span>
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="w-3 h-3 text-emerald-500" />
              GDPR · LOPDGDD · LSSI-CE
            </span>
          </motion.div>
        </div>
      </section>

      {/* Body: sidebar + content */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10 lg:gap-20">
        {/* Sidebar TOC */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <p className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-4">
              {T.index}
            </p>
            <nav className="space-y-1">
              {sections.map((s, i) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  onClick={(e) => handleAnchor(e, s.id)}
                  className={`group flex items-start gap-3 py-1.5 text-[13px] leading-snug transition-colors ${
                    activeId === s.id
                      ? "text-white"
                      : "text-zinc-500 hover:text-zinc-300"
                  }`}
                  data-testid={`legal-toc-${s.id}`}
                >
                  <span
                    className={`w-4 flex-shrink-0 tabular-nums text-[10px] pt-0.5 ${
                      activeId === s.id ? "text-white" : "text-zinc-600"
                    }`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span>{s.heading}</span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Content */}
        <article ref={contentRef} className="max-w-3xl">
          {sections.map((s, i) => (
            <section
              key={s.id}
              id={s.id}
              className="mb-14 md:mb-20 scroll-mt-24"
              data-testid={`legal-section-${s.id}`}
            >
              <div className="flex items-baseline gap-4 mb-5 md:mb-6">
                <span className="text-[11px] tabular-nums text-zinc-600 tracking-widest">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="font-grotesk text-xl md:text-2xl font-semibold text-white tracking-tight">
                  {s.heading}
                </h2>
              </div>
              <div className="legal-prose text-[15px] leading-[1.75] text-zinc-300 space-y-4">
                {s.body}
              </div>
            </section>
          ))}

          {/* Related documents */}
          {related && related.length > 0 && (
            <div className="mt-16 md:mt-24 pt-10 border-t border-white/[0.08]">
              <p className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mb-5">
                {T.related}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {related.map((r) => (
                  <Link
                    key={r.href}
                    to={r.href}
                    className="group border border-white/[0.08] p-5 hover:border-white/25 hover:bg-white/[0.02] transition-all"
                  >
                    <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">
                      {lang === "es" ? "Documento" : "Document"}
                    </p>
                    <p className="text-sm text-white group-hover:text-white flex items-center justify-between">
                      {r.label}
                      <span className="text-zinc-600 group-hover:text-white transition-colors">
                        →
                      </span>
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/[0.06]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-[11px] text-zinc-600">
            © {new Date().getFullYear()} {T.footer}
          </p>
          <div className="flex items-center gap-5 text-[11px]">
            <Link to="/privacidad" className="text-zinc-500 hover:text-white transition-colors">
              {lang === "es" ? "Privacidad" : "Privacy"}
            </Link>
            <Link to="/terminos" className="text-zinc-500 hover:text-white transition-colors">
              {lang === "es" ? "Términos" : "Terms"}
            </Link>
            <Link to="/cookies" className="text-zinc-500 hover:text-white transition-colors">
              Cookies
            </Link>
            <Link to="/legal" className="text-zinc-500 hover:text-white transition-colors">
              {lang === "es" ? "Aviso legal" : "Legal notice"}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

/**
 * Utility: subcomponent styles for legal content bodies
 */
export const P = ({ children }) => <p>{children}</p>;
export const Strong = ({ children }) => (
  <strong className="text-white font-medium">{children}</strong>
);
export const UL = ({ children }) => (
  <ul className="list-none space-y-2 pl-0 my-3">{children}</ul>
);
export const LI = ({ children }) => (
  <li className="flex gap-3">
    <span className="text-zinc-600 select-none">—</span>
    <span>{children}</span>
  </li>
);
export const Callout = ({ children }) => (
  <div className="border-l-2 border-white/25 pl-5 py-1 my-6 text-zinc-400 text-[14px] italic">
    {children}
  </div>
);
export const Table = ({ headers, rows }) => (
  <div className="my-6 border border-white/[0.08] overflow-hidden">
    <table className="w-full text-[13px]">
      <thead>
        <tr className="border-b border-white/[0.08] bg-white/[0.02]">
          {headers.map((h, i) => (
            <th
              key={i}
              className="text-left px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-medium"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr
            key={i}
            className="border-b border-white/[0.05] last:border-b-0 hover:bg-white/[0.015]"
          >
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-zinc-300 align-top">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
